/* eslint-disable react/prop-types */
/**
 * CustomerMessageCard — iter197w
 * ==============================
 * Unified "Customer Message" panel that amalgamates the four customer-
 * portal-bound surfaces under a single mental model, with one shared
 * "Regenerate everything" action that walks the AI surfaces in priority
 * order:
 *
 *   1. CAE (Customer Advice Engine) — the PRIMARY 40-90 word headline
 *      + 100-180 word full story the customer sees on the portal.
 *   2. Contextual Customer Note — only auto-regenerated when FOH has
 *      already typed shorthand into the raw note (we never fabricate a
 *      note from nothing — it would muddy the headline).
 *   3. Standard advisories — deterministic rule-engine toggles. Not
 *      regenerated by AI; rendered as togglable chips so FOH can
 *      remove the ones that don't apply.
 *   4. SATE (recommended approach) — auto-attached deterministic
 *      workflow template. Kept as a collapsed subsection so the
 *      reading flow stays calm.
 *
 * Visual rules
 * ------------
 * • One outer card, one header, one global Regenerate button.
 * • Subsection headers are intentionally subtle — they read like
 *   anchors, not separate cards.
 * • Spacing follows the 2-3x comfortable rule so the surfaces don't
 *   compete with the labour/parts ledger above.
 *
 * Data flow
 * ---------
 * • CAE writes via /cae/regenerate + /cae PUT (preserved).
 * • Contextual note writes via its existing onChange handler (preserved).
 * • Standard advisories via onToggleCustomerNote (preserved).
 * • SATE via its own onChange handler (preserved).
 *
 * This component is composition-only — it does not own schema or state
 * that wasn't already owned by the inner surfaces. The "Regenerate
 * everything" button is a pure coordinator that exposes children
 * imperative handles via refs.
 */
import { useRef, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  MessageSquare,
  Info,
  Sparkles,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "../ui/sonner";
import { API } from "@/contexts/AuthContext";
import CAESection from "./CAESection";
import ContextualCustomerNoteSection from "./ContextualCustomerNoteSection";
import SATESection from "./SATESection";


export default function CustomerMessageCard({
  jobGroup,
  jobIdx,
  yestimateId,
  onToggleCustomerNote,
  onContextualNoteChange,
  onSATEChange,
  onCAEApplied,
}) {
  const customerNotes = jobGroup?.customer_notes || [];
  const hasCustomerNotes = customerNotes.length > 0;
  const activeCount = customerNotes.filter((n) => n.enabled !== false).length;

  const [busyAll, setBusyAll] = useState(false);
  const [showAdvisories, setShowAdvisories] = useState(hasCustomerNotes);

  const cae = jobGroup?.customer_advice;
  const sate = jobGroup?.sate;
  const ccn  = jobGroup?.contextual_customer_note;

  // "Regenerate everything" — sequential so each surface sees a fresh
  // server state before the next request. CAE is the only mandatory
  // call. The contextual note is re-run ONLY when it already has raw
  // input (otherwise we'd be fabricating workshop nuance from nothing).
  const handleRegenerateAll = async () => {
    if (busyAll) return;
    setBusyAll(true);
    let regenerated = 0;
    try {
      // 1) CAE — primary narrative.
      try {
        await axios.post(
          `${API}/foh/yestimate/${yestimateId}/job-group/${jobGroup.id}/cae/regenerate`,
        );
        regenerated += 1;
      } catch (e) {
        console.warn("CAE regenerate failed", e);
      }
      // 2) Contextual note — only if FOH had typed shorthand.
      const rawNote = (ccn?.raw_note || "").trim();
      if (rawNote.length > 0) {
        try {
          await axios.post(
            `${API}/foh/yestimate/${yestimateId}/job-group/${jobGroup.id}/contextual-note/generate`,
            {
              raw_note: rawNote,
              note_type: ccn?.note_type || "Repair Context",
              rewrite_mode: ccn?.rewrite_mode || "Professional Rewrite",
            },
          );
          regenerated += 1;
        } catch (e) {
          console.warn("Contextual note regenerate failed", e);
        }
      }
      // 3) Refresh JG state from the server so all subsections re-render.
      if (typeof onCAEApplied === "function") {
        await onCAEApplied();
      }
      if (regenerated > 0) {
        toast.success(
          `Customer message refreshed (${regenerated} section${regenerated === 1 ? "" : "s"})`,
        );
      } else {
        toast.error("Nothing was regenerated — check connection and try again.");
      }
    } finally {
      setBusyAll(false);
    }
  };

  // Status pills for the header — at-a-glance sense of what's filled in.
  const caeReady = !!cae?.summary;
  const ccnReady = (ccn?.enabled && ccn?.final_text) ? true : false;
  const sateReady = sate && !sate.suppressed;

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white mt-3 overflow-hidden"
      data-testid={`customer-message-card-${jobIdx}`}
    >
      {/* ---- Unified header ------------------------------------------- */}
      <header className="px-4 py-3 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <MessageSquare className="w-4 h-4 text-slate-700 shrink-0" />
          <h3 className="text-[13px] font-bold text-slate-800 uppercase tracking-wide">
            Customer Message
          </h3>
          <span className="text-[11px] text-slate-500 hidden sm:inline">
            — everything the customer reads about this job
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge
            variant="outline"
            className={`text-[10px] ${caeReady ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-slate-100 border-slate-300 text-slate-500"}`}
            data-testid={`cm-status-cae-${jobIdx}`}
          >
            <Sparkles className="w-2.5 h-2.5 mr-1" />
            Headline {caeReady ? "✓" : "—"}
          </Badge>
          <Badge
            variant="outline"
            className={`text-[10px] ${ccnReady ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-slate-100 border-slate-300 text-slate-500"}`}
            data-testid={`cm-status-ccn-${jobIdx}`}
          >
            Workshop note {ccnReady ? "✓" : "—"}
          </Badge>
          <Badge
            variant="outline"
            className={`text-[10px] ${sateReady ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "bg-slate-100 border-slate-300 text-slate-500"}`}
            data-testid={`cm-status-sate-${jobIdx}`}
          >
            Approach {sateReady ? "✓" : "—"}
          </Badge>
          {hasCustomerNotes && (
            <Badge
              variant="outline"
              className="text-[10px] bg-amber-50 border-amber-300 text-amber-800"
              data-testid={`cm-status-advisories-${jobIdx}`}
            >
              {activeCount} advisor{activeCount === 1 ? "y" : "ies"}
            </Badge>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="ml-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            disabled={busyAll}
            onClick={handleRegenerateAll}
            data-testid={`cm-regenerate-all-${jobIdx}`}
            title="Regenerate AI sections (headline, full story, and workshop note if you've drafted one)"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${busyAll ? "animate-spin" : ""}`} />
            {busyAll ? "Refreshing…" : "Regenerate"}
          </Button>
        </div>
      </header>

      {/* ---- Body — composed surfaces, calm vertical rhythm ----------- */}
      <div className="px-4 py-4 space-y-5">
        {/* (1) CAE — primary customer narrative. Sits FIRST because
            it's what the customer sees on the portal above everything
            else, and that mental order maps best for FOH review. */}
        <div data-testid={`cm-cae-block-${jobIdx}`}>
          <CAESection
            jobGroup={jobGroup}
            yestimateId={yestimateId}
            onApplied={onCAEApplied}
          />
        </div>

        {/* (2) SATE — recommended approach (deterministic workflow
            outcomes). Only rendered when the JG actually carries SATE
            data; the inner component already handles that. */}
        <div data-testid={`cm-sate-block-${jobIdx}`}>
          <SATESection
            yestimateId={yestimateId}
            jobGroup={jobGroup}
            jobIdx={jobIdx}
            onChange={onSATEChange}
          />
        </div>

        {/* (3) Workshop context — Contextual Customer Note. The inner
            component already renders its own collapsed header + italic
            description, so this outer wrapper is now a thin mount
            point. iter197z2 tidy: removed the duplicate sub-header
            that made the section look top-heavy. */}
        <div data-testid={`cm-ccn-block-${jobIdx}`}>
          <ContextualCustomerNoteSection
            yestimateId={yestimateId}
            jobGroup={jobGroup}
            jobIdx={jobIdx}
            onChange={onContextualNoteChange}
          />
        </div>

        {/* (4) Standard advisories — deterministic toggles. Collapsed
            by default once there are >0 so FOH isn't visually
            overwhelmed; expand with one click to manage the toggles. */}
        {hasCustomerNotes && (
          <div
            className="border-t border-slate-100 pt-4"
            data-testid={`cm-advisories-block-${jobIdx}`}
          >
            <button
              type="button"
              onClick={() => setShowAdvisories((v) => !v)}
              className="w-full flex items-center justify-between gap-2 text-left group"
              data-testid={`cm-advisories-toggle-${jobIdx}`}
            >
              <div className="flex items-center gap-2">
                <Info className="w-3.5 h-3.5 text-amber-600" />
                <p className="text-[11.5px] font-semibold text-slate-700 uppercase tracking-wide">
                  Standard advisories
                </p>
                <span className="text-[11px] text-slate-500">
                  ({activeCount} of {customerNotes.length} active)
                </span>
              </div>
              {showAdvisories ? (
                <ChevronUp className="w-4 h-4 text-slate-400 group-hover:text-slate-700" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400 group-hover:text-slate-700" />
              )}
            </button>
            {showAdvisories && (
              <>
                <p className="text-[11px] text-slate-500 italic mt-2 mb-2 leading-snug">
                  Auto-derived from the rules engine. Toggle any that don't apply to this job.
                </p>
                <div className="space-y-1.5">
                  {customerNotes.map((note) => {
                    const isOn = note.enabled !== false;
                    return (
                      <label
                        key={note.id || note.key}
                        className={`flex items-start gap-2.5 py-2 px-2.5 rounded border cursor-pointer transition-colors ${
                          isOn
                            ? "bg-amber-50/50 border-amber-200 hover:bg-amber-50"
                            : "bg-slate-100 border-slate-200 text-slate-500"
                        }`}
                        data-testid={`customer-note-toggle-${jobIdx}-${note.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={isOn}
                          onChange={() =>
                            onToggleCustomerNote &&
                            onToggleCustomerNote(jobGroup.id, note.id || note.key)
                          }
                          className="mt-1 h-4 w-4 rounded text-amber-600 focus:ring-amber-500"
                        />
                        <span
                          className={`flex-1 text-[12.5px] leading-snug ${
                            isOn ? "text-slate-800" : "line-through text-slate-500"
                          }`}
                        >
                          {note.text}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
